import type { ReactNode } from 'react';
import type { CredentialType } from '@access-cms/shared-constants';

import { DocumentFieldName, DocumentGroupId, SectionId } from './constants';

export type ReviewStage = 'COR' | 'FNMS' | 'VAS';
export type ReviewStatus = 'PENDING' | 'APPROVED' | 'REJECTED' | 'RETURNED' | 'REASSIGNED' | 'CANCELLED' | 'SUPERSEDED';
export type ApprovalStatusBadgeVariant =
  | 'fnms-save-failed'
  | 'pending-dci-action'
  | 'pending-dci-review'
  | 'pending-federal-approver-review';

export interface ApprovalListItem {
  id?: string;
  userId?: string | null;
  legalLastName?: string;
  legalFirstName?: string;
  contractNumber?: string;
  applicationId?: string;
  entityId?: string;
  pooled?: boolean;
  reviewStage?: ReviewStage;
  status?: ReviewStatus;
  applicationStatus?: string;
  assignedToUserId?: string | null;
  assignedToFirstName?: string | null;
  assignedToLastName?: string | null;
}

export interface ApprovalDetailsData {
  sections: ApprovalDetailsSection[];
}

/** The COR's credential decision — same set as CREDENTIAL_TYPES / CredentialTypeEnum server-side. */
export type PivCardSelection = CredentialType;

export interface ApprovalDetailsAdditionalAccessData {
  hasExternalFederalPiv: boolean;
  /**
   * The credential decision persisted by a prior COR approval (corApproval). The decision
   * is made once: when present it prefills and locks the selection, and the approve payload
   * omits the credential fields (a re-approval after a VAS return must not re-send them).
   */
  persistedPivCardSelection: PivCardSelection | null;
  pivCardSelection: PivCardSelection | null;
  /**
   * ACP-2438: false when the contract is not configured for temporary or seasonal
   * users. The six-month question is then not rendered and the answer is No.
   */
  allowsTemporarySeasonal: boolean;
  workingSixMonths: boolean | null;
}

export interface ApprovalDetailsSection {
  documentGroups?: DocumentGroup[];
  editable?: boolean;
  id: SectionId;
  rows: ApprovalDetailsRow[];
  title: string;
}

export interface ApprovalDetailsAdditionalAccessSection extends ApprovalDetailsSection {
  additionalAccessData: ApprovalDetailsAdditionalAccessData;
  id: SectionId.ADDITIONAL_ACCESS_INFORMATION;
}

export function isAdditionalAccessSection(
  section: ApprovalDetailsSection,
): section is ApprovalDetailsAdditionalAccessSection {
  return section.id === SectionId.ADDITIONAL_ACCESS_INFORMATION;
}

export interface ApprovalDetailsRow {
  /** Canonical field path included in return feedback. */
  fieldPath?: string;
  id: string;
  /** Whether this row can be edited or requested for edit. Defaults to true. */
  isEditable?: boolean;
  label: string;
  value: string | number | ReactNode;
}

export interface DocumentGroup {
  id: DocumentGroupId;
  title: string;
  documents: DocumentFile[];
}

export interface DocumentFile {
  documentFieldPath: DocumentFieldPath;
  fileName: string;
  fileSizeLabel: string;
  fileUrl: string;
  id: string;
  mimeType?: string;
}

export type DocumentFieldPath = `documents.${DocumentFieldName}`;
